import { mapState } from 'vuex';
import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';
import constants from '@/constants';
import { pushStoryInteraction, INTERACTION_TYPE } from '@/helpers/dataLayer';
import { isWrapperApp } from '@/mixins/userAgent';
import { largeNumberDisplay } from '@/utils/helpers';
import {
  CMS_CONTRACT_CLICK_OPEN_ORDERSLIP,
  ColorListContestSummaryType,
  CMS_MARKET_RUNTIME,
} from '../cms-config';
import { initials } from '../cms-market-helpers';
import { buildColorList, withOwnSettlement } from './color-list-model';

export default {
  components: { marketChangePrice },
  inject: { cmsRuntime: { from: CMS_MARKET_RUNTIME } },
  props: {
    settings: { type: Object, required: true },
    market: { type: Object, required: true },
    historyData: { type: Object, default: null },
    openNewTab: { type: Boolean, default: false },
    trackingContext: { type: Object, default: () => ({}) },
  },
  data() {
    return { constants, failedImages: {} };
  },
  watch: {
    market: {
      immediate: true,
      handler(market) {
        this.cmsRuntime.seed([market]);
      },
    },
  },
  computed: {
    ...mapState('app', ['settingShowOdds']),
    displayMarket() {
      return withOwnSettlement(this.cmsRuntime.markets[this.market.marketId], this.market);
    },
    tile() {
      return buildColorList(
        this.settings,
        this.displayMarket,
        this.historyData,
        this.settingShowOdds,
      );
    },
    summaryText() {
      if (this.settings.contestSummaryType !== ColorListContestSummaryType.Volume)
        return this.tile.summaryText;
      const volume = this.historyData?.volume;
      return typeof volume === 'number' && Number.isFinite(volume) && volume >= 0
        ? `${largeNumberDisplay(volume)} shares traded last ${this.tile.numDays} days`
        : '';
    },
    linkTarget() {
      return this.openNewTab && !isWrapperApp ? '_blank' : undefined;
    },
    // A price opens the orderslip ("Click to trade"); without it, prices and the footer go to the market ("Open Market").
    opensOrderslip() {
      return CMS_CONTRACT_CLICK_OPEN_ORDERSLIP;
    },
  },
  methods: {
    route(contractId, direction = 'yes') {
      return {
        name: 'market-details',
        params: { marketId: this.market.marketId, marketUrl: this.displayMarket.marketUrl },
        ...(contractId
          ? { query: { contractId, ...(direction === 'no' ? { no: true } : {}) } }
          : {}),
      };
    },
    track() {
      pushStoryInteraction({
        interactionType: INTERACTION_TYPE.CONTRACT_CURRENT_PRICE_LIST,
        ...this.trackingContext,
      });
    },
    navigate(event) {
      if (event.target.closest('a, button')) return;
      this.track();
      const row = event.target.closest('.cms-colorlist__row');
      this.openDetails(row ? Number(row.dataset.contractId) : undefined, row?.dataset.direction);
    },
    openDetails(contractId, direction) {
      const route = this.route(contractId, direction);
      if (this.linkTarget) window.open(this.$router.resolve(route).href, '_blank', 'noopener');
      else this.$router.push(route);
    },
    disabled(row) {
      return (
        this.tile.state !== constants.MARKET_OPEN ||
        (this.opensOrderslip && !this.cmsRuntime.canTrade(this.displayMarket, row.contract))
      );
    },
    trade(row, event) {
      if (this.disabled(row)) return;
      this.track();
      if (!this.opensOrderslip) {
        this.openDetails(row.id, row.direction);
        return;
      }
      this.cmsRuntime.openOrder({
        marketId: this.market.marketId,
        contractId: row.id,
        direction: row.direction,
        priceCents: row.buyPrice,
        contractImageUrl: row.image,
        trigger: event.currentTarget,
      });
    },
    initials,
  },
};
